import { useCallback, useEffect, useState } from 'react';
import { useDebounce } from 'use-debounce';

/**
 * Return interface for the seed search hook
 */
interface UseSeedSearchReturn {
  /**
   * Current search term (immediate, not debounced)
   * Use this for the search input value to ensure responsive UI
   */
  searchTerm: string;

  /**
   * Debounced search term (delayed by 300ms)
   * Use this for filtering operations to avoid excessive re-renders and API calls
   */
  debouncedSearchTerm: string;

  /**
   * Update the search term
   * @param term - The new search term
   */
  setSearchTerm: (term: string) => void;

  /**
   * Clear the search term (reset to empty string)
   */
  clearSearch: () => void;
}

/**
 * Seed search state management with debouncing
 *
 * Manages search term state with debouncing to optimize filtering performance
 * and avoid excessive API calls during user typing.
 *
 * **Debouncing Strategy:**
 * - `searchTerm`: Updates immediately on every keystroke for responsive input field
 * - `debouncedSearchTerm`: Delayed by 300ms, used for actual filtering operations
 * - Integration: The debounced value syncs back to the filter system via `onDebouncedChange`
 *
 * **Usage Pattern:**
 * ```typescript
 * const { searchTerm, debouncedSearchTerm, setSearchTerm } = useSeedSearch(
 *   filters.search,
 *   (debounced) => setFilters(prev => ({ ...prev, search: debounced }))
 * );
 *
 * // Bind searchTerm to input for responsive UI
 * <SearchBar value={searchTerm} onChange={setSearchTerm} />
 *
 * // The debounced value automatically updates filters, which triggers API calls
 * ```
 *
 * **Performance Considerations:**
 * - 300ms debounce delay balances responsiveness with performance
 * - Prevents excessive API calls during fast typing (one API call per 300ms pause)
 * - Input field remains responsive with immediate updates
 * - Reduces backend load and improves UX
 *
 * **Why This Architecture:**
 * - Search term is part of the filter system (persisted in localStorage + URL)
 * - This hook provides the debouncing layer between user input and filter updates
 * - Keeps filter system as single source of truth while adding performance optimization
 *
 * @param initialSearchTerm - Initial search term from filter state
 * @param onDebouncedChange - Callback to sync debounced value back to filters
 * @returns Search state and management functions
 */
export const useSeedSearch = (
  initialSearchTerm: string = '',
  onDebouncedChange?: (debouncedTerm: string) => void
): UseSeedSearchReturn => {
  // Immediate search term for responsive input field
  const [searchTerm, setSearchTermState] = useState<string>(initialSearchTerm);

  // BUGFIX: Use 0ms delay when clearing search, 300ms when typing
  // This prevents the "empty table" bug when clearing a search that returned no results
  const [debouncedSearchTerm] = useDebounce(
    searchTerm,
    searchTerm === '' ? 0 : 300
  );

  // Sync initial search term changes (when filter is reset or loaded from storage)
  useEffect(() => {
    setSearchTermState(initialSearchTerm);
  }, [initialSearchTerm]);

  // Sync debounced value back to filters
  useEffect(() => {
    if (onDebouncedChange && debouncedSearchTerm !== initialSearchTerm) {
      onDebouncedChange(debouncedSearchTerm);
    }
  }, [debouncedSearchTerm, onDebouncedChange, initialSearchTerm]);

  // Update search term handler
  const setSearchTerm = useCallback((term: string) => {
    setSearchTermState(term);
  }, []);

  // Clear search term helper
  const clearSearch = useCallback(() => {
    setSearchTermState('');
  }, []);

  return {
    searchTerm,
    debouncedSearchTerm,
    setSearchTerm,
    clearSearch,
  };
};
